import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AnimationProvider } from './contexts/AnimationContext';
import { SeasonProvider } from './contexts/SeasonContext';
import MainNav from './components/Common/MainNav';
import Footer from './components/Common/Footer';
import ScrollToTop from './components/Common/ScrollToTop';
import PageTransition from './components/Animation/PageTransition';
import { LoadingSpinner } from './components/Loading/ComponentLoaders';
import { preloadCriticalResources, addResourceHints, collectPerformanceMetrics } from './utils/bundleOptimization';
import { lazyWithRetry } from './utils/lazyWithRetry';
import TrackingProvider from './components/Common/TrackingProvider';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/Common/ToastContainer';
import './App.css';
import './styles/theme.css';
import './styles/themes.css';

// Lazy load pages for better performance
const LazyLandingPage = lazyWithRetry(() => import('./components/Pages/LandingPage'));
const LazyLeagueManagement = lazyWithRetry(() => import('./components/Pages/LeagueManagement'));
const LazyLeagueSettings = lazyWithRetry(() => import('./components/League/LeagueSettings'));
const LazyAuth = lazyWithRetry(() => import('./components/Auth/Auth'));
const LazyAccount = lazyWithRetry(() => import('./components/Auth/Account'));
const LazyPayment = lazyWithRetry(() => import('./components/Payment/Payment'));
const LazyBlog = lazyWithRetry(() => import('./components/Blog/Blog'));
const LazyBlogPost = lazyWithRetry(() => import('./components/Blog/BlogPost'));
const LazyGameRules = lazyWithRetry(() => import('./components/Pages/GameRules'));
// const LazyPricingPage = lazyWithRetry(() => import('./components/Pages/PricingPage')); // Temporarily hidden
const LazyAuctionDraftRoomPage = lazyWithRetry(() => import('./components/Pages/AuctionDraftRoomPage'));
const LazyAuctionDraftRoomUnified = lazyWithRetry(() => import('./components/Pages/AuctionDraftRoomUnified'));
const LazyRecordPicksPage = lazyWithRetry(() => import('./components/Pages/RecordPicksPage'));
const LazyLeaguePlayerPotsPage = lazyWithRetry(() => import('./components/Pages/LeaguePlayerPotsPage'));
const LazyFixtures = lazyWithRetry(() => import('./components/Pages/Fixtures'));
const LazyMyTeamStandings = lazyWithRetry(() => import('./components/Pages/MyTeamStandings'));
const LazyStatsPage = lazyWithRetry(() => import('./components/Pages/StatsPage'));
const LazyPlayerTiersPage = lazyWithRetry(() => import('./components/Pages/PlayerTiersPageSimple'));
const LazyJoinLeaguePage = lazyWithRetry(() => import('./components/Pages/JoinLeaguePage'));
const LazyDemoPage = lazyWithRetry(() => import('./components/Pages/DemoPage'));
const LazyDraftRecapPage = lazyWithRetry(() => import('./components/Pages/DraftRecapPage'));
const LazyDraftReplayPage = lazyWithRetry(() => import('./components/Pages/DraftReplayPage'));
const LazyLiveStandingsPage = lazyWithRetry(() => import('./components/Pages/LiveStandingsPage'));
const LazyWaiverWirePage = lazyWithRetry(() => import('./components/Pages/WaiverWirePage'));
const LazyTradeCenterPage = lazyWithRetry(() => import('./components/Pages/TradeCenterPage'));
const LazyMatchupPage = lazyWithRetry(() => import('./components/Pages/MatchupPage'));
const LazyLeagueMessageBoardPage = lazyWithRetry(() => import('./components/Pages/LeagueMessageBoardPage'));
const LazyGwHistoryPage = lazyWithRetry(() => import('./components/Pages/GwHistoryPage'));
const LazyFdrCalendarPage = lazyWithRetry(() => import('./components/Pages/FdrCalendarPage'));
const LazyPlayerProjectionsPage = lazyWithRetry(() => import('./components/Pages/PlayerProjectionsPage'));
const LazyLineupsPage = lazyWithRetry(() => import('./components/Pages/LineupsPage'));
const LazyGwPointsDashboardPage = lazyWithRetry(() => import('./components/Pages/GwPointsDashboardPage'));
const LazyTransferHistoryPage = lazyWithRetry(() => import('./components/Pages/TransferHistoryPage'));
const LazyDraftAnalyticsPage = lazyWithRetry(() => import('./components/Pages/DraftAnalyticsPage'));
const LazyPlayerNewsFeedPage = lazyWithRetry(() => import('./components/Pages/PlayerNewsFeedPage'));
const LazyLeagueHomePage = lazyWithRetry(() => import('./components/Pages/LeagueHomePage'));
const LazyPointsLeaguePage = lazyWithRetry(() => import('./components/Pages/PointsLeaguePage'));
const LazyCaptainPicksPage = lazyWithRetry(() => import('./components/Pages/CaptainPicksPage'));
const LazyLeagueTimelinePage = lazyWithRetry(() => import('./components/Pages/LeagueTimelinePage'));
const LazyAdminDashboardPage = lazyWithRetry(() => import('./components/Pages/AdminDashboardPage'));

// Protected route component for league settings (commissioners only)
const ProtectedLeagueSettings = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/auth" />;
  }
  
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <LazyLeagueSettings />
    </Suspense>
  );
};

function App() {
  const { user, loading } = useAuth();

  // Performance optimizations on app load
  useEffect(() => {
    // Add resource hints for faster loading
    addResourceHints();
    
    // Preload critical resources
    preloadCriticalResources();
    
    // Collect performance metrics (development only)
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        collectPerformanceMetrics();
      }, 2000);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <div className="text-neutral-400 text-sm">Loading FOOTY DRAFT HUB...</div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackMessage="We're experiencing technical difficulties with the Fantasy Draft Hub. Our team has been notified.">
      <ToastProvider>
      <ThemeProvider>
        <AnimationProvider>
          <SeasonProvider>
            <Router>
            <TrackingProvider />
            <ScrollToTop />
            <div className="min-h-screen bg-slate-950 flex flex-col">
              {/* "Launch Year 2026/27 — Free" banner hidden for the time
                  being. Restoring it means uncommenting this div AND
                  reverting MainNav.js's nav back to `top-9` (it's currently
                  `top-0` to fill the space) AND this <main>'s paddingTop
                  back to 102px (currently 66px) — the banner's 36px height
                  is baked into both of those. */}
              {/* <div className="fixed inset-x-0 top-0 z-[60] h-9 bg-emerald-500 flex items-center justify-center px-4">
                <span className="text-xs sm:text-sm font-semibold text-slate-950 text-center truncate">
                  Launch Year 2026/27 — Free
                </span>
              </div> */}
              <MainNav />
              <main className="flex-grow" style={{ paddingTop: '66px' }}>
                <PageTransition>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={<LazyLandingPage user={user} />} />
                      <Route path="/auth" element={!user ? <LazyAuth /> : <Navigate to="/" />} />
                      <Route path="/account" element={user ? <LazyAccount /> : <Navigate to="/auth" />} />
                      <Route path="/leagues" element={user ? <LazyLeagueManagement /> : <Navigate to="/auth" />} />
                      <Route path="/leagues/:leagueId/settings" element={<ProtectedLeagueSettings />} />
                      <Route path="/payment" element={user ? <LazyPayment /> : <Navigate to="/auth" />} />
                      <Route path="/blog" element={<LazyBlog />} />
                      <Route path="/blog/:slug" element={<LazyBlogPost />} />
                      <Route path="/rules" element={<LazyGameRules />} />
                      {/* <Route path="/pricing" element={<LazyPricingPage />} /> */} {/* Temporarily hidden */}
                      <Route path="/auction-draft/:roomId" element={user ? <LazyAuctionDraftRoomPage /> : <Navigate to="/auth" />} />
                      <Route path="/auction-draft-v2/:roomId" element={user ? <LazyAuctionDraftRoomPage /> : <Navigate to="/auth" />} />
                      <Route path="/auction-draft-unified/:roomId" element={user ? <LazyAuctionDraftRoomUnified /> : <Navigate to="/auth" />} />
                      <Route path="/fixtures" element={<LazyFixtures />} />
                      <Route path="/stats" element={<LazyStatsPage />} />
                      <Route path="/player-tiers" element={<LazyPlayerTiersPage />} />
                      <Route path="/my-team-standings/:leagueId" element={user ? <LazyMyTeamStandings /> : <Navigate to="/auth" />} />
                      <Route path="/draft-room/:leagueId" element={user ? <LazyAuctionDraftRoomUnified /> : <Navigate to="/auth" />} />
                      {/* No login gate: public leagues' Record Picks page is
                          viewable read-only via a shared link without an
                          account. The page itself checks league_type/role
                          and renders access-denied for private leagues. */}
                      <Route path="/manual-draft/:leagueId" element={<LazyRecordPicksPage />} />
                      <Route path="/leagues/:leagueId/player-pots" element={user ? <LazyLeaguePlayerPotsPage /> : <Navigate to="/auth" />} />
                      {/* No login gate: this is the "share link" for public
                          leagues too (LeagueCard.js falls back to the raw
                          league id when there's no invite_code), so a
                          logged-out visitor needs to see the league preview
                          before being asked to sign up to join. */}
                      <Route path="/join/:inviteCode" element={<LazyJoinLeaguePage />} />
                      <Route path="/demo" element={<LazyDemoPage />} />
                      <Route path="/draft-recap/:leagueId" element={user ? <LazyDraftRecapPage /> : <Navigate to="/auth" />} />
                      <Route path="/draft-replay/:leagueId" element={user ? <LazyDraftReplayPage /> : <Navigate to="/auth" />} />
                      <Route path="/live-standings/:leagueId" element={user ? <LazyLiveStandingsPage /> : <Navigate to="/auth" />} />
                      <Route path="/waiver-wire/:leagueId" element={user ? <LazyWaiverWirePage /> : <Navigate to="/auth" />} />
                      <Route path="/trades/:leagueId" element={user ? <LazyTradeCenterPage /> : <Navigate to="/auth" />} />
                      <Route path="/matchup/:leagueId" element={user ? <LazyMatchupPage /> : <Navigate to="/auth" />} />
                      <Route path="/messages/:leagueId" element={user ? <LazyLeagueMessageBoardPage /> : <Navigate to="/auth" />} />
                      <Route path="/gw-history/:leagueId" element={user ? <LazyGwHistoryPage /> : <Navigate to="/auth" />} />
                      <Route path="/fdr" element={<LazyFdrCalendarPage />} />
                      <Route path="/projections" element={<LazyPlayerProjectionsPage />} />
                      <Route path="/lineups" element={<LazyLineupsPage />} />
                      <Route path="/player-news" element={<LazyPlayerNewsFeedPage />} />
                      <Route path="/league/:leagueId/home" element={user ? <LazyLeagueHomePage /> : <Navigate to="/auth" />} />
                      <Route path="/gw-points/:leagueId" element={user ? <LazyGwPointsDashboardPage /> : <Navigate to="/auth" />} />
                      <Route path="/transfers/:leagueId" element={user ? <LazyTransferHistoryPage /> : <Navigate to="/auth" />} />
                      <Route path="/draft-analytics/:leagueId" element={user ? <LazyDraftAnalyticsPage /> : <Navigate to="/auth" />} />
                      <Route path="/points-league/:leagueId" element={user ? <LazyPointsLeaguePage /> : <Navigate to="/auth" />} />
                      <Route path="/captains/:leagueId" element={user ? <LazyCaptainPicksPage /> : <Navigate to="/auth" />} />
                      <Route path="/timeline/:leagueId" element={user ? <Suspense fallback={<div>Loading...</div>}><LazyLeagueTimelinePage /></Suspense> : <Navigate to="/auth" />} />
                      <Route path="/admin" element={user ? <LazyAdminDashboardPage /> : <Navigate to="/auth" />} />
                    </Routes>
                  </Suspense>
                </PageTransition>
              </main>
              <Footer />
            </div>
            </Router>
          </SeasonProvider>
        </AnimationProvider>
      </ThemeProvider>
      <ToastContainer />
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;